import { getFragmentData, gql, type FragmentType } from '@plato/app-core/gql';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useUser } from '@plato/app-core/hooks/use-user';
import { omitEmpty } from '@plato/shared-util/object';
import type { QuickFilter } from '@plato/ui-legacy/components/filters/lib/filter-utils';
import type { AnyFilter } from '@plato/ui-legacy/components/filters/types/filter-types';
import type { UiFilterVisibility } from '@plato/ui-legacy/components/filters/types/ui-filter-visibility';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@plato/ui/containers/alert-dialog';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
} from '@plato/ui/containers/dialog';
import { Join } from '@plato/ui/containers/join';
import {
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverIconClose,
    PopoverTitle,
    PopoverTrigger,
} from '@plato/ui/containers/popover';
import { Button } from '@plato/ui/primitives/button';
import { FieldLabel } from '@plato/ui/primitives/field';
import { Input } from '@plato/ui/primitives/input';
import {
    Menu,
    MenuDivider,
    MenuGroup,
    MenuItem,
    MenuItemLabel,
    MenuItemLeading,
    MenuItemTrailing,
} from '@plato/ui/primitives/menu';
import { ToggleButton } from '@plato/ui/primitives/toggle-button';
import { ToggleSwitch } from '@plato/ui/primitives/toggle-switch';
import { cn } from '@plato/ui/util/classes';
import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import { isEqual } from 'lodash-es';
import {
    CheckIcon,
    ChevronDownIcon,
    PencilIcon,
    PlusIcon,
    RotateCcwIcon,
    SaveIcon,
    Trash2Icon,
    UsersIcon,
} from 'lucide-react';
import {
    useEffect,
    useEffectEvent,
    useRef,
    useState,
    type ReactElement,
    type RefObject,
    type SubmitEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { useAnalytics } from '../hooks/use-analytics.ts';

const savedFilterView = gql(/* GraphQL */ `
    fragment SavedFilterViews_FilterView on FilterView {
        id
        name
        visibility
        filters
        createdBy {
            id
        }
    }
`);

const createFilterViewMutation = gql(/* GraphQL */ `
    mutation CreateFilterView($input: CreateFilterViewInput!) {
        createFilterView(input: $input) {
            id
            name
            visibility
            filters
        }
    }
`);

const updateFilterViewMutation = gql(/* GraphQL */ `
    mutation UpdateFilterView($input: UpdateFilterViewInput!) {
        updateFilterView(input: $input) {
            id
            name
            visibility
            filters
        }
    }
`);

const deleteFilterViewMutation = gql(/* GraphQL */ `
    mutation DeleteFilterView($id: ID!) {
        deleteFilterView(id: $id)
    }
`);

export const filterViewTypenames = { additionalTypenames: ['FilterView'] };

export type SavedFilterViewData = FragmentType<typeof savedFilterView>;

export type SavedFilterViewsProps<TFilter extends AnyFilter> = {
    views: SavedFilterViewData[] | undefined;
    viewKey: string;
    appliedFilters: TFilter;
    defaultFilters?: TFilter;
    onApplyFilters: (filters: TFilter) => void;
    quickFilters?: QuickFilter<TFilter>[];
};

type SavedFilterView<TFilter> = {
    id: string;
    name: string;
    visibility: UiFilterVisibility;
    filters: TFilter;
    isOwn: boolean;
};

type ViewSelection = { type: 'view'; id: string } | { type: 'preset'; id: string } | { type: 'none' };

const noSelection: ViewSelection = { type: 'none' };

function filtersMatch(a: AnyFilter, b: AnyFilter): boolean {
    return isEqual(omitEmpty(a), omitEmpty(b));
}

function getModifiedKeys(saved: AnyFilter, applied: AnyFilter): string[] {
    const savedFilters = omitEmpty(saved);
    const appliedFilters = omitEmpty(applied);
    const keys = new Set([...Object.keys(savedFilters), ...Object.keys(appliedFilters)]);

    return [...keys].filter((key) => !isEqual(savedFilters[key], appliedFilters[key])).toSorted();
}

function hasOnlyDefaultKeys(filters: AnyFilter, defaultFilters: AnyFilter = {}): boolean {
    const defaultKeys = Object.keys(omitEmpty(defaultFilters));

    return Object.keys(omitEmpty(filters)).every((key) => defaultKeys.includes(key));
}

function withDirtyMark(name: string, isDirty: boolean): string {
    return isDirty ? `${name} *` : name;
}

export function SavedFilterViews<TFilter extends AnyFilter>({
    views,
    viewKey,
    appliedFilters,
    defaultFilters,
    onApplyFilters,
    quickFilters = [],
}: SavedFilterViewsProps<TFilter>): ReactElement {
    const { t } = useTranslation('shared');
    const user = useUser();
    const { canShareFilterViews } = useAppConfig();
    const isDesktop = useBreakpoint('sm');
    const { toast } = useToast();
    const sendEvent = useAnalytics();
    const [{ fetching: isCreating }, executeCreate] = useMutation(createFilterViewMutation);
    const [{ fetching: isUpdating }, executeUpdate] = useMutation(updateFilterViewMutation);
    const [{ fetching: isDeleting }, executeDelete] = useMutation(deleteFilterViewMutation);

    const [open, setOpen] = useState(false);
    const [isNameDialogOpen, setIsNameDialogOpen] = useState(false);
    const [editingView, setEditingView] = useState<SavedFilterView<TFilter>>();
    const [deletingView, setDeletingView] = useState<SavedFilterView<TFilter>>();
    const [selection, setSelection] = useState<ViewSelection>();
    const [filtersBeforeApply, setFiltersBeforeApply] = useState<TFilter>();

    if (filtersBeforeApply !== undefined && !filtersMatch(filtersBeforeApply, appliedFilters)) {
        setFiltersBeforeApply(undefined);
    }

    const presets = quickFilters.filter((preset) => preset.showByDefault !== false);
    const savedViews: SavedFilterView<TFilter>[] = (views ?? []).map((node) => {
        const view = getFragmentData(savedFilterView, node);

        return {
            id: view.id,
            name: view.name,
            visibility: view.visibility,
            filters: view.filters as TFilter,
            isOwn: view.createdBy.id === user.id,
        };
    });
    const myViews = savedViews.filter((view) => view.isOwn);
    const sharedViews = savedViews.filter((view) => !view.isOwn);

    const matchingView = savedViews.find((view) => filtersMatch(view.filters, appliedFilters));
    const matchingPreset = matchingView
        ? undefined
        : presets.find((preset) => filtersMatch(preset.filter, appliedFilters));
    const matchingSelection: ViewSelection = matchingView
        ? { type: 'view', id: matchingView.id }
        : matchingPreset
          ? { type: 'preset', id: matchingPreset.id }
          : noSelection;

    if (selection === undefined && matchingSelection.type !== 'none') {
        setSelection(matchingSelection);
    }

    const currentSelection = selection ?? matchingSelection;
    const activeView =
        currentSelection.type === 'view'
            ? savedViews.find((view) => view.id === currentSelection.id)
            : undefined;
    const activePreset =
        currentSelection.type === 'preset'
            ? presets.find((preset) => preset.id === currentSelection.id)
            : undefined;
    const activeName = activeView?.name ?? activePreset?.label;
    const activeFilters = activeView?.filters ?? activePreset?.filter;
    const isApplyingFilters = filtersBeforeApply !== undefined;
    const hasRemovedAllFilters =
        !isApplyingFilters &&
        activeFilters !== undefined &&
        hasOnlyDefaultKeys(appliedFilters, defaultFilters) &&
        !hasOnlyDefaultKeys(activeFilters, defaultFilters);

    if (hasRemovedAllFilters) {
        setSelection(noSelection);
    }

    const isDirty =
        !isApplyingFilters && activeFilters !== undefined && !filtersMatch(activeFilters, appliedFilters);
    const modifiedViewId = isDirty ? activeView?.id : undefined;
    const ownActiveView = activeView?.isOwn ? activeView : undefined;
    const showPresetsInMenu = !isDesktop && presets.length > 0;
    const isViewSelected = activeView !== undefined || (!isDesktop && activePreset !== undefined);

    const sendViewModified = useEffectEvent(() => {
        if (activeView === undefined) {
            return;
        }

        sendEvent('filter_view_modified', {
            view_id: activeView.id,
            view_key: viewKey,
            visibility: activeView.visibility,
            modified_keys: getModifiedKeys(activeView.filters, appliedFilters),
        });
    });

    useEffect(() => {
        if (modifiedViewId !== undefined) {
            sendViewModified();
        }
    }, [modifiedViewId]);

    function viewsLabel(): string {
        if (activeView) {
            return withDirtyMark(activeView.name, isDirty);
        }
        if (!isDesktop && activePreset) {
            return withDirtyMark(activePreset.label, isDirty);
        }
        if (isDesktop && presets.length > 0) {
            return t('saved_views__more_views');
        }
        return t('saved_views__all_views');
    }

    async function createView(name: string, visibility: UiFilterVisibility): Promise<void> {
        const result = await executeCreate(
            { input: { name, visibility, filters: appliedFilters, viewKey } },
            filterViewTypenames,
        );

        if (!result.data) {
            toast({ icon: 'warning', title: t('saved_views__save_error') });
            return;
        }

        const created = result.data.createFilterView;
        setSelection({ type: 'view', id: created.id });
        toast({ icon: 'success', title: t('filter_view_updated_title', { ns: 'common' }) });
        sendEvent('filter_view_created', {
            view_id: created.id,
            view_key: viewKey,
            visibility: created.visibility,
        });
        setIsNameDialogOpen(false);
    }

    function applyFilters(filters: TFilter): void {
        if (!filtersMatch(filters, appliedFilters)) {
            setFiltersBeforeApply(appliedFilters);
        }

        onApplyFilters(filters);
    }

    async function saveChanges(view: SavedFilterView<TFilter>): Promise<void> {
        setOpen(false);
        const result = await executeUpdate(
            { input: { id: view.id, name: view.name, visibility: view.visibility, filters: appliedFilters } },
            filterViewTypenames,
        );

        if (!result.data) {
            toast({ icon: 'warning', title: t('saved_views__save_error') });
            return;
        }

        toast({ icon: 'success', title: t('filter_view_updated_title', { ns: 'common' }) });
        sendEvent('filter_view_updated', {
            view_id: view.id,
            view_key: viewKey,
            visibility: view.visibility,
        });
    }

    async function editView(
        view: SavedFilterView<TFilter>,
        name: string,
        visibility: UiFilterVisibility,
    ): Promise<void> {
        const result = await executeUpdate(
            { input: { id: view.id, name, visibility, filters: view.filters } },
            filterViewTypenames,
        );

        if (!result.data) {
            toast({ icon: 'warning', title: t('saved_views__save_error') });
            return;
        }

        toast({ icon: 'success', title: t('saved_views__edit_success') });
        sendEvent('filter_view_updated', { view_id: view.id, view_key: viewKey, visibility });
        setEditingView(undefined);
    }

    function revert(): void {
        setOpen(false);

        if (activeFilters !== undefined) {
            applyFilters(activeFilters);
        }
    }

    function deselect(): void {
        setSelection(noSelection);

        if (defaultFilters !== undefined) {
            applyFilters(defaultFilters);
        }
    }

    async function deleteView(view: SavedFilterView<TFilter>): Promise<void> {
        const result = await executeDelete({ id: view.id }, filterViewTypenames);

        if (!result.data) {
            toast({ icon: 'warning', title: t('saved_views__delete_error') });
            return;
        }

        sendEvent('filter_view_deleted', { view_id: view.id, view_key: viewKey });
        setDeletingView(undefined);

        if (activeView?.id === view.id) {
            setSelection(noSelection);
        }
    }

    function applyView(view: SavedFilterView<TFilter>): void {
        setOpen(false);

        if (activeView?.id === view.id) {
            deselect();
            return;
        }

        setSelection({ type: 'view', id: view.id });
        applyFilters(view.filters);
        sendEvent('filter_view_applied', {
            view_id: view.id,
            view_key: viewKey,
            visibility: view.visibility,
        });
    }

    function applyPreset(preset: QuickFilter<TFilter>): void {
        setOpen(false);

        if (activePreset?.id === preset.id) {
            deselect();
            return;
        }

        setSelection({ type: 'preset', id: preset.id });
        applyFilters(preset.filter);
    }

    function openNameDialog(): void {
        setOpen(false);
        setIsNameDialogOpen(true);
    }

    function openEditDialog(view: SavedFilterView<TFilter>): void {
        setOpen(false);
        setEditingView(view);
    }

    function openDeleteDialog(view: SavedFilterView<TFilter>): void {
        setOpen(false);
        setDeletingView(view);
    }

    const viewsTrigger = (
        <PopoverTrigger render={<ToggleButton checked={isViewSelected} className="min-w-0 shrink" />}>
            <span className={cn('truncate', !isViewSelected && 'text-content-weaker')}>{viewsLabel()}</span>
            <ChevronDownIcon
                className={cn(
                    'size-4 shrink-0 text-content-weaker transition-transform duration-state ease-state',
                    open && 'rotate-180',
                )}
            />
        </PopoverTrigger>
    );

    const viewsContent = (
        <PopoverContent aria-label={t('saved_views__all_views')} className="sm:w-72">
            <PopoverHeader>
                <PopoverTitle>{t('saved_views__all_views')}</PopoverTitle>
                <PopoverIconClose />
            </PopoverHeader>
            <PopoverBody className="p-0 sm:px-0">
                <Menu>
                    {myViews.length > 0 && (
                        <MenuGroup label={t('saved_views__my_views')}>
                            {myViews.map((view) => (
                                <FilterViewRow
                                    key={view.id}
                                    label={view.name}
                                    selected={activeView?.id === view.id}
                                    shared={view.visibility === 'team'}
                                    onClick={() => {
                                        applyView(view);
                                    }}
                                />
                            ))}
                        </MenuGroup>
                    )}
                    {sharedViews.length > 0 && (
                        <MenuGroup label={t('saved_views__shared_views')}>
                            {sharedViews.map((view) => (
                                <FilterViewRow
                                    key={view.id}
                                    label={view.name}
                                    selected={activeView?.id === view.id}
                                    onClick={() => {
                                        applyView(view);
                                    }}
                                />
                            ))}
                        </MenuGroup>
                    )}
                    {showPresetsInMenu && (
                        <MenuGroup label={t('saved_views__presets')}>
                            {presets.map((preset) => (
                                <FilterViewRow
                                    key={preset.id}
                                    label={preset.label}
                                    selected={activePreset?.id === preset.id}
                                    onClick={() => {
                                        applyPreset(preset);
                                    }}
                                />
                            ))}
                        </MenuGroup>
                    )}
                    {(showPresetsInMenu || savedViews.length > 0) && <MenuDivider />}
                    <MenuGroup
                        label={activeName ? withDirtyMark(activeName, isDirty) : t('saved_views__view')}
                    >
                        <MenuItem label={t('saved_views__save_as_new')} onClick={openNameDialog}>
                            <MenuItemLeading>
                                <PlusIcon />
                            </MenuItemLeading>
                            <MenuItemLabel />
                        </MenuItem>
                        {ownActiveView && isDirty && (
                            <MenuItem
                                label={t('saved_views__save_changes')}
                                disabled={isUpdating}
                                onClick={() => {
                                    void saveChanges(ownActiveView);
                                }}
                            >
                                <MenuItemLeading>
                                    <SaveIcon />
                                </MenuItemLeading>
                                <MenuItemLabel />
                            </MenuItem>
                        )}
                        {isDirty && (
                            <MenuItem label={t('saved_views__revert')} onClick={revert}>
                                <MenuItemLeading>
                                    <RotateCcwIcon />
                                </MenuItemLeading>
                                <MenuItemLabel />
                            </MenuItem>
                        )}
                        {ownActiveView && (
                            <MenuItem
                                label={t('saved_views__edit')}
                                onClick={() => {
                                    openEditDialog(ownActiveView);
                                }}
                            >
                                <MenuItemLeading>
                                    <PencilIcon />
                                </MenuItemLeading>
                                <MenuItemLabel />
                            </MenuItem>
                        )}
                        {ownActiveView && (
                            <MenuItem
                                label={t('saved_views__delete')}
                                className="text-danger"
                                onClick={() => {
                                    openDeleteDialog(ownActiveView);
                                }}
                            >
                                <MenuItemLeading className="text-danger">
                                    <Trash2Icon />
                                </MenuItemLeading>
                                <MenuItemLabel />
                            </MenuItem>
                        )}
                    </MenuGroup>
                </Menu>
            </PopoverBody>
        </PopoverContent>
    );

    return (
        <>
            <Popover open={open} onOpenChange={setOpen}>
                {isDesktop ? (
                    <Join>
                        {presets.map((preset) => (
                            <ToggleButton
                                key={preset.id}
                                checked={activePreset?.id === preset.id}
                                aria-pressed={activePreset?.id === preset.id}
                                onClick={() => {
                                    applyPreset(preset);
                                }}
                            >
                                {withDirtyMark(preset.label, activePreset?.id === preset.id && isDirty)}
                            </ToggleButton>
                        ))}
                        {viewsTrigger}
                    </Join>
                ) : (
                    viewsTrigger
                )}
                {viewsContent}
            </Popover>
            <FilterViewNameDialog
                open={isNameDialogOpen}
                onOpenChange={setIsNameDialogOpen}
                title={t('save_view_dialog__title')}
                canShareFilterViews={canShareFilterViews}
                isPending={isCreating}
                onSubmit={(name, visibility) => {
                    void createView(name, visibility);
                }}
            />
            <FilterViewNameDialog
                open={editingView !== undefined}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setEditingView(undefined);
                    }
                }}
                title={t('edit_view_dialog__title')}
                initialName={editingView?.name}
                initialVisibility={editingView?.visibility}
                canShareFilterViews={canShareFilterViews}
                isPending={isUpdating}
                onSubmit={(name, visibility) => {
                    if (editingView) {
                        void editView(editingView, name, visibility);
                    }
                }}
            />
            <DeleteFilterViewDialog
                open={deletingView !== undefined}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setDeletingView(undefined);
                    }
                }}
                isPending={isDeleting}
                onConfirm={() => {
                    if (deletingView) {
                        void deleteView(deletingView);
                    }
                }}
            />
        </>
    );
}

type FilterViewRowProps = {
    label: string;
    selected: boolean;
    shared?: boolean;
    onClick: () => void;
};

function FilterViewRow({ label, selected, shared = false, onClick }: FilterViewRowProps): ReactElement {
    return (
        <MenuItem label={label} aria-selected={selected} onClick={onClick}>
            <MenuItemLabel />
            {(shared || selected) && (
                <MenuItemTrailing>
                    {shared && <UsersIcon />}
                    {selected && <CheckIcon className="stroke-[2.5] text-brand" />}
                </MenuItemTrailing>
            )}
        </MenuItem>
    );
}

type FilterViewNameDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    initialName?: string;
    initialVisibility?: UiFilterVisibility;
    canShareFilterViews: boolean;
    isPending: boolean;
    onSubmit: (name: string, visibility: UiFilterVisibility) => void;
};

function FilterViewNameDialog({
    open,
    onOpenChange,
    title,
    ...formProps
}: FilterViewNameDialogProps): ReactElement {
    const { t } = useTranslation('shared');
    const nameInputRef = useRef<HTMLInputElement>(null);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md" initialFocus={nameInputRef}>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogIconClose label={t('form__close', { ns: 'common' })} />
                </DialogHeader>
                <FilterViewNameForm nameInputRef={nameInputRef} {...formProps} />
            </DialogContent>
        </Dialog>
    );
}

type FilterViewNameFormProps = Omit<FilterViewNameDialogProps, 'open' | 'onOpenChange' | 'title'> & {
    nameInputRef: RefObject<HTMLInputElement | null>;
};

function FilterViewNameForm({
    initialName = '',
    initialVisibility = 'only_me',
    canShareFilterViews,
    isPending,
    onSubmit,
    nameInputRef,
}: FilterViewNameFormProps): ReactElement {
    const { t } = useTranslation('shared');
    const [name, setName] = useState(initialName);
    const [shareWithTeam, setShareWithTeam] = useState(initialVisibility === 'team');
    const trimmedName = name.trim();

    function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
        event.preventDefault();

        if (!trimmedName) {
            return;
        }

        onSubmit(trimmedName, shareWithTeam ? 'team' : 'only_me');
    }

    return (
        <form className="contents" onSubmit={handleSubmit}>
            <DialogBody className="flex flex-col gap-default">
                <Input
                    ref={nameInputRef}
                    value={name}
                    maxLength={255}
                    placeholder={t('save_view_dialog__name_placeholder')}
                    onChange={(event) => {
                        setName(event.target.value);
                    }}
                />
                {canShareFilterViews && (
                    <FieldLabel className="items-center">
                        <ToggleSwitch
                            variant="primary"
                            checked={shareWithTeam}
                            onChange={(event) => {
                                setShareWithTeam(event.target.checked);
                            }}
                        />
                        {t('save_view_dialog__share_with_team')}
                    </FieldLabel>
                )}
            </DialogBody>
            <DialogFooter>
                <DialogClose>{t('form__cancel', { ns: 'common' })}</DialogClose>
                <Button type="submit" variant="primary" disabled={!trimmedName || isPending}>
                    {t('save_view_dialog__save_button')}
                </Button>
            </DialogFooter>
        </form>
    );
}

type DeleteFilterViewDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    isPending: boolean;
    onConfirm: () => void;
};

function DeleteFilterViewDialog({
    open,
    onOpenChange,
    isPending,
    onConfirm,
}: DeleteFilterViewDialogProps): ReactElement {
    const { t } = useTranslation('shared');

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('saved_views__delete_confirmation_title')}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {t('saved_views__delete_confirmation_description')}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>{t('form__cancel', { ns: 'common' })}</AlertDialogCancel>
                    <AlertDialogAction
                        variant="primary"
                        intent="destructive"
                        disabled={isPending}
                        onClick={onConfirm}
                    >
                        {t('form__delete', { ns: 'common' })}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
